import { useState, useEffect } from 'react';
import { User, Save, Building2, Mail, Phone, FileText } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { useAuthStore } from '../store/authStore';
import { unidadesApi } from '../api/backend';

export function PerfilPage() {
  const { user, isCliente, isAdmin } = useAuthStore();
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    nome_responsavel: '',
    cpf: '',
    email: '',
    telefone: '',
    cargo: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unidadeId, setUnidadeId] = useState<number | null>(null);

  useEffect(() => {
    loadPerfil();
  }, [user]);

  const loadPerfil = async () => {
    try {
      setLoading(true);
      
      // Se for cliente (unidade logada), buscar dados da unidade
      if (isCliente() && user?.unidade_id) {
        const unidade = await unidadesApi.get(user.unidade_id)

        if (unidade) {
          setUnidadeId(unidade.id);
          setFormData({
            nome: unidade.nome || '',
            cnpj: unidade.cnpj || '',
            nome_responsavel: unidade.responsavel_nome || '',
            cpf: unidade.responsavel_cpf || '',
            email: unidade.responsavel_email || '',
            telefone: unidade.responsavel_telefone || '',
            cargo: unidade.responsavel_cargo || '',
          });
        }

        /*
        const response = await fetch('/api/unidades');
        const data = await response.json();
        if (data.success) {
          const unidade = data.data.find((u: any) => u.id === user.unidade_id);
          if (unidade) {
            setUnidadeId(unidade.id);
            setFormData({
              nome: unidade.nome || '',
              cnpj: unidade.cnpj || '',
              nome_responsavel: unidade.nome_responsavel || '',
              cpf: unidade.cpf || '',
              email: unidade.email || '',
              telefone: unidade.telefone || '',
              cargo: unidade.cargo || '',
            });
          }
        }
        */
      }
    } catch (error) {
      console.error('Error loading perfil:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!unidadeId) {
      alert('Erro: ID da unidade não encontrado');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`https://backend.youware.com/api/unidades/${unidadeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ativo: true, // Manter ativo
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Perfil atualizado com sucesso!');
        loadPerfil(); // Recarregar dados
      } else {
        alert('Erro ao atualizar perfil: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating perfil:', error);
      alert('Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
              {user?.photo_url ? (
                <img
                  src={user.photo_url}
                  alt="Perfil"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Meu Perfil
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {formData.nome || 'Perfil da Unidade'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados da Unidade */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Dados da Unidade
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome da Unidade
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isAdmin() ? 'Nome pode ser editado na tela de Gerenciar Unidades' : 'Nome da unidade não pode ser alterado pelo usuário'}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    CNPJ da Empresa
                  </label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg ${
                      isAdmin() 
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                    } focus:ring-2 focus:ring-primary-500`}
                    placeholder="00.000.000/0000-00"
                    readOnly={!isAdmin()}
                    disabled={!isAdmin()}
                  />
                  {!isAdmin() && (
                    <p className="text-xs text-gray-500 mt-1">CNPJ não pode ser alterado pelo usuário</p>
                  )}
                </div>
              </div>
            </div>

            {/* Dados do Responsável */}
            <div className="pb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Dados do Responsável
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    value={formData.nome_responsavel}
                    onChange={(e) => setFormData({ ...formData, nome_responsavel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="Nome do responsável"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CPF
                  </label>
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cargo
                  </label>
                  <input
                    type="text"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: Gerente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Salvar Alterações</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
